/**
 * Search Bar Component
 * Searches collections and/or products via Shopify Predictive Search JSON API
 */
if (!customElements.get('search-bar')) {
  class SearchBar extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('.search-bar__input');
      this.clearBtn = this.querySelector('.search-bar__clear');
      this.resultsContainer = this.querySelector('.search-bar__results');
      this.timeout = null;
      this.isOpen = false;
      this.abortController = null;

      // Settings from data attributes
      this.searchTypes = (this.dataset.searchTypes || 'collection,product').split(',').map(s => s.trim());
      this.limit = parseInt(this.dataset.limit || '6', 10);
      this.noResultsText = this.dataset.noResultsText || 'No results found';
      this.collectionsTitle = this.dataset.collectionsTitle || 'Collections';
      this.productsTitle = this.dataset.productsTitle || 'Products';
      this.viewAllText = this.dataset.viewAllText || 'View all results';

      this.bindEvents();
    }

    bindEvents() {
      this.input.addEventListener('input', () => {
        clearTimeout(this.timeout);
        const query = this.input.value.trim();

        this.toggleClearButton(query.length > 0);

        if (query.length < 2) {
          this.close();
          return;
        }

        this.timeout = setTimeout(() => {
          this.search(query);
        }, 300);
      });

      if (this.clearBtn) {
        this.clearBtn.addEventListener('click', () => {
          this.input.value = '';
          this.toggleClearButton(false);
          this.close();
          this.input.focus();
        });
      }

      document.addEventListener('click', (e) => {
        if (!this.contains(e.target)) {
          this.close();
        }
      });

      this.input.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.close();
          this.input.blur();
        }
      });
    }

    toggleClearButton(show) {
      if (this.clearBtn) {
        this.clearBtn.classList.toggle('is-visible', show);
      }
    }

    async search(query) {
      if (this.abortController) {
        this.abortController.abort();
      }
      this.abortController = new AbortController();

      this.showLoading();

      try {
        const typesParam = this.searchTypes.join(',');
        const url = `${Shopify.routes.root}search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=${typesParam}&resources[limit]=${this.limit}`;

        const response = await fetch(url, {
          signal: this.abortController.signal
        });

        if (!response.ok) throw new Error(response.status);

        const data = await response.json();
        this.renderResults(data, query);

      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('Search Bar: Error fetching results', error);
        this.showNoResults();
      }
    }

    renderResults(data, query) {
      const resources = data.resources?.results || {};
      const collections = resources.collections || [];
      const products = resources.products || [];

      let html = '';
      let hasResults = false;

      // Collections
      if (this.searchTypes.includes('collection') && collections.length > 0) {
        hasResults = true;
        html += `<div class="search-bar__results-group">`;
        html += `<h6 class="search-bar__results-title">${this.escapeHtml(this.collectionsTitle)}</h6>`;
        html += `<ul class="search-bar__results-list">`;

        collections.forEach(collection => {
          const url = collection.url || '';
          const name = collection.title || '';
          const imgSrc = collection.image ? this.getImageUrl(collection.image, '96x96') : '';
          const count = collection.published_products_count;

          html += `<li>`;
          html += `<a href="${url}" class="search-bar__result-item">`;
          if (imgSrc) {
            html += `<div class="search-bar__result-image"><img src="${imgSrc}" alt="${this.escapeHtml(name)}" loading="lazy" width="48" height="48"></div>`;
          }
          html += `<div class="search-bar__result-info">`;
          html += `<p class="search-bar__result-name">${this.escapeHtml(name)}</p>`;
          if (count !== undefined) {
            html += `<p class="search-bar__result-meta">${count} products</p>`;
          }
          html += `</div>`;
          html += `</a>`;
          html += `</li>`;
        });

        html += `</ul></div>`;
      }

      // Products
      if (this.searchTypes.includes('product') && products.length > 0) {
        hasResults = true;
        html += `<div class="search-bar__results-group">`;
        html += `<h6 class="search-bar__results-title">${this.escapeHtml(this.productsTitle)}</h6>`;
        html += `<ul class="search-bar__results-list">`;

        products.forEach(product => {
          const url = product.url || '';
          const name = product.title || '';
          const imgSrc = product.image ? this.getImageUrl(product.image, '96x96') : (product.featured_image?.url ? this.getImageUrl(product.featured_image.url, '96x96') : '');
          const price = product.price ? this.formatMoney(product.price) : '';
          const comparePrice = product.compare_at_price_max && product.compare_at_price_max !== '0.00' && product.compare_at_price_max !== product.price ? this.formatMoney(product.compare_at_price_max) : '';

          html += `<li>`;
          html += `<a href="${url}" class="search-bar__result-item">`;
          if (imgSrc) {
            html += `<div class="search-bar__result-image"><img src="${imgSrc}" alt="${this.escapeHtml(name)}" loading="lazy" width="48" height="48"></div>`;
          }
          html += `<div class="search-bar__result-info">`;
          html += `<p class="search-bar__result-name">${this.escapeHtml(name)}</p>`;
          html += `</div>`;
          if (price) {
            html += `<div class="search-bar__result-price">`;
            if (comparePrice) {
              html += `<s class="search-bar__result-price--compare">${comparePrice}</s> `;
            }
            html += `<span>${price}</span>`;
            html += `</div>`;
          }
          html += `</a>`;
          html += `</li>`;
        });

        html += `</ul></div>`;
      }

      if (!hasResults) {
        this.showNoResults();
        return;
      }

      // "View all" link
      const encodedQuery = encodeURIComponent(query);
      html += `<a href="${Shopify.routes.root}search?q=${encodedQuery}" class="search-bar__view-all">${this.escapeHtml(this.viewAllText)}</a>`;

      this.resultsContainer.innerHTML = html;
      this.open();
    }

    getImageUrl(src, size) {
      if (!src) return '';
      // Handle Shopify CDN image URLs
      if (typeof src === 'object' && src.url) {
        src = src.url;
      }
      if (src.includes('cdn.shopify.com')) {
        // Insert size parameter before file extension
        return src.replace(/(\.\w+)(\?|$)/, `_${size}$1$2`);
      }
      return src;
    }

    formatMoney(cents) {
      if (typeof cents === 'string') {
        cents = cents.replace('.', '');
      }
      const amount = (parseInt(cents, 10) / 100).toFixed(2);

      // Use Shopify's money format if available
      if (window.Shopify?.currency?.active) {
        return `${amount} ${window.Shopify.currency.active}`;
      }

      // Fallback: use the shop's money format
      if (window.theme?.moneyFormat) {
        return window.theme.moneyFormat.replace('{{amount}}', amount).replace('{{ amount }}', amount);
      }

      return `$${amount}`;
    }

    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }

    showLoading() {
      this.resultsContainer.innerHTML = `<div class="search-bar__loading">Loading...</div>`;
      this.open();
    }

    showNoResults() {
      this.resultsContainer.innerHTML = `<div class="search-bar__no-results">${this.escapeHtml(this.noResultsText)}</div>`;
      this.open();
    }

    open() {
      this.isOpen = true;
      this.resultsContainer.classList.add('is-open');
    }

    close() {
      this.isOpen = false;
      this.resultsContainer.classList.remove('is-open');
    }
  }

  customElements.define('search-bar', SearchBar);
}
